// ErrorStrip: the DS §2.1 Error standard, a system or integration failure. A 3 px --red rule on --card-2, the
// one-line error, **Retry**, and an *Error details* disclosure holding the mono rows Correlation ID · status ·
// retries · error details · last attempt. Words: "Failed sync is visible and retryable." F carries the "error" tone
// only; G5's Sync (#82) composes this strip for its failure form and adds its other tones and *Manual recovery*
// through `actions` and `className`. Not a numbered row: its gallery entry is a variant under #52. Given a server
// `problem`, it is an alert whose details carry the request id and status, never an internal error's own detail. The
// sentence and the request id show once: a Correlation ID or error details row the one-line error already says is
// not drawn again.

import type { ReactNode } from "react";
import { ERROR_LABELS } from "../labels";
import { cx } from "../lib/cx";
import { Button, type Blocked } from "./Button";
import { Disclosure } from "./Disclosure";
import { Icon } from "./icons";
import { KeyValue, type KeyValueItem } from "./KeyValue";
import type { Hs } from "../lib/hs";

export interface ErrorDetails {
  correlationId?: ReactNode | undefined;
  status?: ReactNode | undefined;
  retries?: ReactNode | undefined;
  /** The error details line. */
  error?: ReactNode | undefined;
  /** When it was last tried (a formatted date and time). */
  lastAttempt?: ReactNode | undefined;
}

/** A typed problem response from the tool door (status, refusal code, detail, request id). */
export interface ErrorProblem {
  status: number;
  refusal_code: string;
  detail?: string | undefined;
  request_id?: string | null | undefined;
}

export interface ErrorStripProps {
  /** The one-line error, in his words. */
  message: ReactNode;
  tone?: "error" | undefined;
  /** Shows **Retry**; absent, there is nothing to retry from here. */
  onRetry?: (() => void) | undefined;
  /** The retry is in flight: aria-busy, label kept. */
  retrying?: boolean | undefined;
  /** The seat may not retry; Retry is absent. */
  canRetry?: boolean | undefined;
  retryBlocked?: Blocked | undefined;
  /** The Error details rows; absent rows are left out. */
  details?: ErrorDetails | undefined;
  /** Error details open at first (default closed). */
  defaultOpen?: boolean | undefined;
  /** More actions after Retry (G5: *Manual recovery*). */
  actions?: ReactNode | undefined;
  /** role="alert": the failure appeared while the person was here. */
  live?: boolean | undefined;
  /**
   * A server problem: forces the alert, fills Correlation ID (request_id) and status, shows `detail` unless the code
   * is INTERNAL or the status is 5xx, and lets Retry show by default for a 5xx or a lost connection (status 0).
   */
  problem?: ErrorProblem | undefined;
  hs?: Hs | undefined;
  className?: string | undefined;
}

export function ErrorStrip({
  message, tone = "error", onRetry, retrying, canRetry, retryBlocked, details: given, defaultOpen, actions, live, problem, hs, className,
}: ErrorStripProps) {
  const internal = problem ? problem.refusal_code === "INTERNAL" || problem.status >= 500 : false;
  const details: ErrorDetails | undefined = problem
    ? {
      ...given,
      correlationId: problem.request_id ?? given?.correlationId,
      status: problem.status,
      error: internal ? undefined : (problem.detail ?? given?.error),
    }
    : given;
  const retryable = canRetry ?? (problem ? problem.status >= 500 || problem.status === 0 : undefined);
  const alert = Boolean(live) || problem !== undefined;
  const said = (value: ReactNode) =>
    typeof message === "string" && (typeof value === "string" || typeof value === "number") && String(value) !== ""
    && message.includes(String(value));
  const rows: KeyValueItem[] = [];
  if (details?.correlationId !== undefined && !said(details.correlationId)) rows.push([ERROR_LABELS.correlationId, details.correlationId]);
  if (details?.status !== undefined) rows.push([ERROR_LABELS.status, details.status]);
  if (details?.retries !== undefined) rows.push([ERROR_LABELS.retries, details.retries]);
  if (details?.error !== undefined && !said(details.error)) rows.push([ERROR_LABELS.details, details.error]);
  if (details?.lastAttempt !== undefined) rows.push([ERROR_LABELS.lastAttempt, details.lastAttempt]);
  return (
    <div className={cx("mc-err", `mc-err-${tone}`, className)} role={alert ? "alert" : undefined}>
      <div className="mc-err-row">
        <Icon name="alert" size={18} />
        <p className="mc-err-t">{message}</p>
        <div className="mc-err-a">
          {onRetry ? (
            <Button sm icon="refresh" onClick={onRetry} busy={retrying} can={retryable} blocked={retryBlocked} hs={hs}>
              {ERROR_LABELS.retry}
            </Button>
          ) : null}
          {actions}
        </div>
      </div>
      {rows.length ? (
        <Disclosure sm title={ERROR_LABELS.errorDetails} defaultOpen={defaultOpen}>
          <KeyValue rows mono items={rows} />
        </Disclosure>
      ) : null}
    </div>
  );
}
